import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowRight, Brain, Network, Search, Sparkles, ShieldCheck, Activity, Users, Trophy } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import api from "@/lib/api";
import { usePricing } from "@/components/Pricing";

function FoundersCountdown() {
  return (
    <Link to="/pricing" data-testid="pricing-strip"
      className="block bg-blaze text-white hover:brightness-110 transition-all">
      <div className="px-4 py-2.5 flex items-center justify-center gap-2 flex-wrap text-center">
        <span className="heading text-xs tracking-[0.2em]">COACHES FREE · JOB POSTING FREE · PROGRAM RECRUITING $5/MO</span>
        <span className="heading text-[10px] tracking-widest underline underline-offset-2">SEE PRICING →</span>
      </div>
    </Link>
  );
}

const agents = [
  { icon: Search, name: "HeadhunterAgent", desc: "Continuously scouts USILA, NILCA, school dept pages, and Inside Lacrosse to build profiles of passive coaches." },
  { icon: Brain, name: "MatchEngineAgent", desc: "GNN + LLM reasoning over the Temporal Knowledge Graph. Every match cites the evidence behind it." },
  { icon: Network, name: "CoachProfileAgent", desc: "Time-stamps every season, championship, and player developed into a dynamic career graph." },
  { icon: ShieldCheck, name: "VettingAgent", desc: "Flags discrepancies between user-claimed data and public records. Suggests tailored interview questions." },
  { icon: Activity, name: "ProgramNeedsAgent", desc: "Conversational AI that turns 'rebuilding, academic-first culture' into queryable TKG attributes." },
  { icon: Users, name: "EntityResolutionAgent", desc: "Dedupes scouted vs. claimed coach profiles in the TKG so your shortlist stays clean." },
  { icon: Sparkles, name: "ConciergeAgent", desc: "Always-on AD chat that answers grounded questions about your pipeline, candidates, and next steps." },
];

const COACH_TRAITS = [
  "Exquisite self-awareness and continuous self-reflection.",
  "High emotional intelligence — perceptive, intuitive, and curious about every athlete on the roster.",
  "Broad strategic vision balanced with a relentless focus on the details that win games.",
  "Nuanced, crisp, superb communication — locker room to film room to parents.",
  "Highest regard, sincere care, and respect for every athlete they coach.",
  "Creative, innovative learner — quick to study, deep and wide in method.",
  "Student of coaching and adjacent disciplines — sees every season as a two-way exchange.",
  "Humble, open, nurturing, and grateful — checks the ego at the field gate.",
  "Views coaching as a calling, an art, and a discipline — not just a job.",
  "Walks the talk — models the standard of life they ask their athletes to live.",
];

export default function Technology({ claimCoach }) {
  const pricing = usePricing();
  const claimFirst = claimCoach?.name ? claimCoach.name.split(" ")[0] : null;
  const [live, setLive] = useState(null);
  const [testimonials, setTestimonials] = useState([]);
  useEffect(() => {
    api.get("/public/stats").then(({ data }) => setLive(data)).catch(() => {});
    api.get("/public/testimonials").then(({ data }) => setTestimonials(data || [])).catch(() => {});
  }, []);
  const heroStats = [
    { v: live ? live.coaches.toLocaleString() : "—", l: "Coach profiles in our system", live: true },
    { v: live ? live.open_jobs.toLocaleString() : "—", l: "Coaching jobs open right now", live: true },
    { v: "7", l: "Specialized AI agents" },
    { v: "$0", l: "To sign up + build your profile" },
  ];
  return (
    <div className="min-h-screen bg-appbg text-white">
      <Header />
      <FoundersCountdown />

      {/* Sport identifier strip */}
      <div
        className="border-b border-volt/25 bg-volt/[0.06] px-4 md:px-8 lg:px-12 py-2 flex items-center justify-center gap-3 text-center"
        data-testid="lacrosse-banner"
      >
        <span className="hidden sm:block h-px w-8 bg-volt/40" />
        <p className="text-[11px] md:text-xs tracking-[0.25em] text-neutral-300">
          <span className="text-volt heading">LACROSSE</span> COACH RECRUITMENT — MEN&apos;S &amp; WOMEN&apos;S, COLLEGE TO YOUTH
          <span className="hidden md:inline text-neutral-500"> · MORE SPORTS COMING SOON</span>
        </p>
        <span className="hidden sm:block h-px w-8 bg-volt/40" />
      </div>

      {/* HERO */}
      <section className="relative min-h-[calc(100vh-72px)] overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-50"
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1623156884380-a080a56ddc93?fm=jpg&q=70&w=2000&auto=format&fit=crop')" }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/85 to-black/30" />
        <div className="absolute inset-0 grid-lines opacity-40" />
        <div className="relative px-4 md:px-8 lg:px-12 pt-12 lg:pt-24 pb-16 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-7">
            <div className="text-xs tracking-[0.3em] text-blaze mb-4" data-testid="hero-eyebrow">
              <span>AI COACH CONNECT · BIDIRECTIONAL MATCHING · LACROSSE FIRST</span>
              <span className="hidden md:inline text-neutral-600 mx-3">·</span>
              <span className="block md:inline text-neutral-300 mt-1 md:mt-0" data-testid="hero-arena-motif">FOR THE ONES IN THE ARENA</span>
            </div>
            {claimFirst ? (
              <h1 className="heading text-5xl sm:text-6xl lg:text-7xl leading-[0.95]" data-testid="hero-claim-personalized">
                BUILT FOR COACHES<br />
                <span className="text-blaze">LIKE YOU, {claimFirst.toUpperCase()}.</span>
              </h1>
            ) : (
              <h1 className="heading text-5xl sm:text-6xl lg:text-7xl xl:text-8xl leading-[0.95]">
                SCHOOLS FIND COACHES.<br />
                <span className="text-blaze">COACHES FIND SCHOOLS.</span><br />THE AI MATCHES BOTH.
              </h1>
            )}
            {claimCoach && (
              <p className="mt-4 text-base md:text-lg text-neutral-200 max-w-xl leading-relaxed" data-testid="hero-claim-subline">
                We mapped your career{claimCoach.current_organization ? ` at ${claimCoach.current_organization}` : ""} from public records
                {claimCoach?.qbr?.score ? (
                  <> and rated it <b className="text-volt">CIR {claimCoach.qbr.score} · {claimCoach.qbr.tier}</b></>
                ) : null}
                . Your profile is ready — review it, correct it, own it.
              </p>
            )}
            <p className="mt-6 text-base md:text-lg text-neutral-300 max-w-xl leading-relaxed" data-testid="hero-anonymity-line">
              An agentic AI Coach Connect platform — seven specialized agents working over a Temporal Knowledge Graph to introduce the right coach to the right program. <b className="text-white">100% anonymous on both sides until you see something worth pursuing.</b> Coaches: nobody knows you&apos;re looking. Programs: nobody knows you&apos;re searching. Identities unlock only when both parties are ready.
            </p>
            <p
              className="mt-3 text-sm text-neutral-400 max-w-xl leading-relaxed"
              data-testid="hero-learns-line"
            >
              <span className="text-volt heading tracking-widest text-xs">LIVING SYSTEM ·</span>{" "}
              The platform learns every day — every coach who claims a profile, every AD who posts a need, every endorsement and interview signal sharpens the match. Tomorrow&apos;s ranking is smarter than today&apos;s.
            </p>
            <div className="mt-4 inline-flex items-center gap-3 border border-volt px-3 py-2 bg-volt/5">
              <span className="heading text-volt text-2xl">$0</span>
              <span className="text-xs uppercase tracking-widest text-neutral-300">Coaches are free — always. Programs post jobs free and add Program Recruiting for $5/month when they want to search proactively.</span>
            </div>

            <div className="mt-8">
              <div className="flex items-center gap-2 mb-3" data-testid="cta-select-label">
                <span className="heading tracking-[0.3em] text-sm text-white">SELECT A BUTTON BELOW</span>
                <span className="text-volt animate-bounce leading-none">▼</span>
              </div>
              <div className="flex flex-wrap items-stretch gap-4">
                <Link
                  to="/auth?role=coach"
                  data-testid="hero-coach-cta"
                  className="inline-flex flex-col items-start bg-blaze text-white heading px-9 py-4 glow-blaze shadow-lg ring-2 ring-white/20 hover:ring-white/50 transition-all hover:-translate-y-1 hover:brightness-110"
                >
                  <span className="inline-flex items-center gap-2 text-xl md:text-2xl">I&apos;M A COACH — CLAIM MY PROFILE <ArrowRight size={20} /></span>
                  <span className="text-[10px] md:text-[11px] tracking-[0.2em] text-white/85 mt-1.5">FREE FOR COACHES · FULL MATCH DETAILS · NO CARD</span>
                </Link>
                <Link
                  to="/auth?role=athletic_director"
                  data-testid="hero-ad-cta"
                  className="inline-flex items-center gap-2 bg-volt text-white heading text-lg px-6 py-4 glow-volt shadow-lg ring-2 ring-white/20 hover:ring-white/50 transition-all hover:-translate-y-1 hover:brightness-110"
                >
                  I&apos;M HIRING · FIND COACHES <ArrowRight size={18} />
                </Link>
                <Link
                  to="/club"
                  data-testid="hero-club-cta"
                  className="relative inline-flex items-center gap-2 bg-white text-black heading text-lg px-6 py-4 shadow-lg ring-2 ring-white/40 hover:ring-white transition-all hover:-translate-y-1"
                >
                  I&apos;M A CLUB · FIND COACHES <ArrowRight size={18} />
                  <span className="absolute -top-2 -right-2 bg-blaze text-white px-1.5 py-0.5 text-[9px] tracking-widest heading">BETA</span>
                </Link>
              </div>
              <Link
                to="/auth?role=coach&player=1"
                data-testid="hero-player-strip"
                className="mt-4 inline-flex items-center gap-2 text-sm text-neutral-300 hover:text-white border border-neutral-700 hover:border-blaze px-4 py-2.5 transition-colors"
              >
                <span className="text-blaze heading tracking-widest text-xs">CURRENT PLAYER?</span>
                Find a summer coaching job — free <ArrowRight size={14} />
              </Link>
            </div>

            <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl" data-testid="hero-live-stats">
              {heroStats.map((s) => (
                <div key={s.l} className={`border-l-2 ${s.live ? "border-volt" : "border-blaze"} pl-4`}>
                  <div className="heading text-3xl md:text-4xl text-mono">{s.v}</div>
                  <div className="text-[10px] md:text-xs uppercase tracking-widest text-neutral-400 mt-1">
                    {s.l}
                    {s.live && <span className="text-volt ml-1.5" title="Live count">● LIVE</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5 hidden lg:block">
            <div className="border border-neutral-800 bg-black/60 backdrop-blur-md p-6 relative">
              <div className="absolute -top-3 left-4 bg-blaze text-white px-3 py-0.5 text-[10px] tracking-widest heading">EXAMPLE MATCH · ILLUSTRATIVE</div>
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="text-xs text-neutral-500 tracking-widest">D1 · WOMEN&apos;S LACROSSE · HEAD COACH</div>
                  <div className="heading text-2xl mt-1">RIVA HOLLOWAY → ASCENT COLLEGE</div>
                  <div className="text-[10px] text-neutral-600 mt-1 tracking-widest">FICTIONAL · FOR PRODUCT ILLUSTRATION ONLY</div>
                </div>
                <div className="text-mono text-blaze text-5xl heading">94</div>
              </div>
              <div className="space-y-3 text-sm">
                <div className="border-l-2 border-volt pl-3">
                  <div className="text-volt text-xs tracking-widest">PHILOSOPHY ALIGNMENT</div>
                  <div className="text-neutral-300 mt-1">Draw-control offense matches program&apos;s stated &lsquo;pace + possession&rsquo; goal (TKG: 2022-2025 stints).</div>
                </div>
                <div className="border-l-2 border-volt pl-3">
                  <div className="text-volt text-xs tracking-widest">CAREER TRAJECTORY</div>
                  <div className="text-neutral-300 mt-1">Two first-ever NCAA berths in last 5 years. Pattern: turnaround → contender.</div>
                </div>
                <div className="border-l-2 border-blaze pl-3">
                  <div className="text-blaze text-xs tracking-widest">WATCHOUT</div>
                  <div className="text-neutral-300 mt-1">No prior conference-of-record exposure; admit-cycle ramp may need 18 months.</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* COACH FUNNEL — 3 steps to active */}
      <section className="px-4 md:px-8 lg:px-12 py-12 border-t border-neutral-800 bg-[#0C0C0E]" data-testid="coach-funnel-steps">
        <div className="text-xs tracking-[0.3em] text-blaze mb-6">HOW COACHES GET HIRED HERE</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 max-w-6xl">
          {[
            { n: "01", t: "CREATE YOUR FREE PROFILE", d: "60 seconds. 100% anonymous — your school, parents, and players are never notified you joined." },
            { n: "02", t: "AI BUILDS YOUR CASE", d: "ProfileAgent enriches your coaching history from public sources; the MatchEngine scores you against live openings 24/7." },
            { n: "03", t: "SEE EVERY MATCH — FREE", d: "Program names, match rationale and anonymous replies are free for every coach. No card, ever." },
          ].map((s, i) => (
            <div key={s.n} className={`bg-appcard p-6 border-l-4 ${i === 2 ? "border-blaze" : "border-neutral-700"}`} data-testid={`funnel-step-${s.n}`}>
              <div className={`heading text-4xl ${i === 2 ? "text-blaze" : "text-neutral-500"}`}>{s.n}</div>
              <div className="heading text-xl mt-2">{s.t}</div>
              <p className="text-sm text-neutral-400 mt-2 leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
        <div className="mt-8">
          <Link to="/auth?role=coach" data-testid="funnel-steps-cta" className="inline-flex items-center gap-2 bg-blaze text-white heading text-lg px-8 py-4 hover:glow-blaze transition-all">
            START FREE — 60 SECONDS <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* MANTRA BAND — bidirectional fit is the core message everywhere */}
      <section className="relative px-4 md:px-8 lg:px-12 py-16 md:py-20 border-t-4 border-blaze" data-testid="landing-mantra">
        <div className="absolute top-0 right-0 h-1 w-1/2 bg-gradient-to-l from-volt to-blaze" />
        <div className="max-w-6xl mx-auto text-center">
          <div className="inline-block chip-blue mb-6">FIT GOES BOTH WAYS</div>
          <h2 className="heading text-3xl sm:text-5xl lg:text-6xl leading-tight">
            The <span className="text-blaze">right coach</span>,<br />
            for the <span className="text-volt">right program</span>,<br />
            at the <span className="text-white">right time</span>.
          </h2>
          <p className="text-neutral-200 text-base md:text-lg mt-8 max-w-3xl mx-auto leading-relaxed">
            We help schools identify coaches who aren&apos;t just <span className="text-blaze font-medium">available</span> — but truly <span className="text-blaze font-medium">aligned</span>.
            And we help coaches find opportunities that fit their <span className="text-volt font-medium">goals, values, careers, and families</span>.
          </p>
        </div>
      </section>

      {/* AGENTS */}
      <section className="relative px-4 md:px-8 lg:px-12 py-20 border-t-4 border-blaze">
        <div className="absolute top-0 right-0 h-1 w-1/3 bg-gradient-to-r from-blaze to-volt" />
        <div className="max-w-3xl">
          <div className="inline-block chip-blue">SEVEN AGENTS · ONE GRAPH</div>
          <h2 className="heading text-4xl md:text-5xl lg:text-6xl mt-3">
            NOT A JOB BOARD. <span className="text-blaze">A WORKFORCE.</span>
          </h2>
          <p className="text-neutral-200 mt-4 max-w-xl">Each agent runs continuously, owns its specialty, and writes back to the Temporal Knowledge Graph — so the next agent reasons over richer evidence.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mt-12">
          {agents.map((a, i) => (
            <div key={a.name} className={`bg-appcard p-6 border-l-4 hover:shadow-[0_0_0_1px_rgb(var(--app-accent-rgb))] transition-all group ${i % 2 === 0 ? "border-blaze" : "border-volt"}`} data-testid={`agent-card-${i}`}>
              <a.icon className={`${i % 2 === 0 ? "text-blaze" : "text-volt"} group-hover:scale-110 transition-transform`} size={28} strokeWidth={1.75} />
              <div className="heading text-2xl mt-4">{a.name}</div>
              <div className="text-neutral-200 text-sm mt-2 leading-relaxed">{a.desc}</div>
            </div>
          ))}
        </div>
      </section>

      {/* COACH EXCELLENCE FRAMEWORK */}
      <section className="relative px-4 md:px-8 lg:px-12 py-20 border-t-4 border-volt bg-appbg" data-testid="excellence-framework">
        <div className="absolute top-0 left-0 h-1 w-1/3 bg-gradient-to-l from-volt to-blaze" />
        <div className="max-w-3xl">
          <div className="inline-block chip-red">COACH EXCELLENCE FRAMEWORK</div>
          <h2 className="heading text-4xl md:text-5xl lg:text-6xl mt-3">
            TEN TRAITS. <span className="text-volt">ZERO GUESSWORK.</span>
          </h2>
          <p className="text-neutral-200 mt-4 max-w-2xl text-base">
            Our match engine doesn&apos;t just look at wins and losses. It evaluates a coach against the ten attributes the best programs in the country actually hire on.
          </p>
        </div>
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 max-w-5xl">
          {COACH_TRAITS.map((t, i) => (
            <div key={t} className={`bg-appcard p-4 flex gap-3 border-l-4 hover:shadow-[0_0_0_1px_rgb(var(--app-primary-rgb))] transition-all ${i % 2 === 0 ? "border-blaze" : "border-volt"}`} data-testid={`trait-${i + 1}`}>
              <div className={`heading text-3xl tabular-nums w-10 shrink-0 ${i % 2 === 0 ? "text-blaze" : "text-volt"}`}>{String(i + 1).padStart(2, "0")}</div>
              <div className="text-sm text-neutral-100 leading-snug">{t}</div>
            </div>
          ))}
        </div>
      </section>
      <section className="px-4 md:px-8 lg:px-12 py-20 border-t border-neutral-800 bg-[#0C0C0E]">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div>
            <Users className="text-blaze" size={32} strokeWidth={1.5} />
            <div className="heading text-3xl mt-4">FOR COACHES</div>
            <div className="mt-3 inline-flex items-center gap-2 text-[10px] tracking-widest text-volt border border-volt/40 px-2 py-1">
              <span aria-hidden="true">🔒</span> ANONYMOUS · NOBODY KNOWS YOU&apos;RE LOOKING
            </div>
            <ol className="space-y-3 text-neutral-300 mt-4">
              <li><span className="text-mono text-blaze mr-2">01</span> Create your free profile + philosophy — <b className="text-white">no one is notified you signed up</b>.</li>
              <li><span className="text-mono text-blaze mr-2">02</span> ProfileAgent enriches it from public sources. You approve every addition.</li>
              <li><span className="text-mono text-blaze mr-2">03</span> Get matched to opportunities with a 0–100 score — and the receipts. <b className="text-white">Your name only reaches a program when you say so.</b></li>
            </ol>
          </div>
          <div>
            <Trophy className="text-volt" size={32} strokeWidth={1.5} />
            <div className="heading text-3xl mt-4">FOR PROGRAMS</div>
            <div className="mt-3 inline-flex items-center gap-2 text-[10px] tracking-widest text-volt border border-volt/40 px-2 py-1">
              <span aria-hidden="true">🔒</span> ANONYMOUS · NOBODY KNOWS YOU&apos;RE SEARCHING
            </div>
            <ol className="space-y-3 text-neutral-300 mt-4">
              <li><span className="text-mono text-volt mr-2">01</span> Tell ProgramNeedsAgent what you actually want — in plain English. <b className="text-white">Your program name never leaves your dashboard.</b></li>
              <li><span className="text-mono text-volt mr-2">02</span> See a ranked candidate list (claimed + scouted) in seconds — <b className="text-white">no candidate knows they were surfaced</b>.</li>
              <li><span className="text-mono text-volt mr-2">03</span> Get vetting reports, interview questions, and market intel. Identities unlock only when both sides engage.</li>
            </ol>
          </div>
          <div>
            <Network className="text-white" size={32} strokeWidth={1.5} />
            <div className="heading text-3xl mt-4">THE TKG</div>
            <p className="text-neutral-300 mt-4 leading-relaxed">
              Every entity — coach, school, season, championship, philosophy — is a node. Every relationship is timestamped.
              The MatchEngine traverses paths through time. That&apos;s how we explain why.
            </p>
          </div>
        </div>
      </section>

      {/* IN THE ARENA — Roosevelt thread */}
      <section
        className="px-4 md:px-8 lg:px-12 py-20 md:py-24 border-t border-neutral-900 bg-gradient-to-b from-[#0A0503] via-black to-black"
        data-testid="arena-quote-section"
      >
        <div className="max-w-4xl mx-auto">
          <div className="text-xs tracking-[0.4em] text-blaze mb-6">FROM THE MAN IN THE ARENA · TEDDY ROOSEVELT, 1910</div>
          <blockquote className="border-l-4 border-blaze pl-6 md:pl-10 space-y-5">
            <p className="text-neutral-100 text-lg md:text-2xl leading-relaxed">
              &ldquo;It is not the critic who counts; not the man who points out how the strong man
              stumbles, or where the doer of deeds could have done them better.
              <span className="text-blaze"> The credit belongs to the man who is actually in the arena</span>,
              whose face is marred by dust and sweat and blood;
            </p>
            <p className="text-neutral-100 text-lg md:text-2xl leading-relaxed">
              who strives valiantly; who errs, who comes short again and again, because there is no effort without
              error and shortcoming; but who does actually strive to do the deeds; who knows great enthusiasms, the
              great devotions; who spends himself in a good cause;
            </p>
            <p className="text-neutral-100 text-lg md:text-2xl leading-relaxed">
              who at the best knows in the end the triumph of high achievement, and who at the worst, if he fails,
              at least fails while daring greatly, so that his place shall never be with those
              <span className="text-blaze"> cold and timid souls who neither know victory nor defeat</span>.&rdquo;
            </p>
          </blockquote>
          <div className="mt-8 text-sm text-neutral-400 max-w-2xl leading-relaxed">
            This is who we built CoachConnect for. Coaches with dust on their cleats. Programs willing to
            back the ones who show up. Everything downstream — the CIR, the matching, the anonymity — exists
            to make sure the ones actually in the arena are the ones who get seen.
          </div>
        </div>
      </section>

      {/* PRICING TEASER */}
      <section className="px-4 md:px-8 lg:px-12 py-20 border-t border-neutral-800" data-testid="technology-pricing">
        <div className="text-xs tracking-widest text-blaze">PRICING</div>
        <h2 className="heading text-4xl md:text-5xl mt-3 mb-3">COACHES FREE. <span className="text-blaze">PROGRAMS RECRUIT FOR $5.</span></h2>
        <p className="text-neutral-400 max-w-3xl">{pricing?.offer_line || "Coaches are free. Posting coaching jobs is free for every program. Program Recruiting is $5/month per program, cancel anytime."}</p>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-px bg-neutral-800 border border-neutral-800">
          {(pricing?.plans || []).map((p) => (
            <div key={p.key} className={`p-8 ${p.key === "program_recruiting" ? "bg-appcard" : "bg-appbg"}`} data-testid={`tech-plan-${p.key}`}>
              <div className={`text-xs tracking-widest ${p.audience === "coach" ? "text-volt" : "text-blaze"}`}>{p.label.toUpperCase()}</div>
              <div className="heading text-4xl mt-2">{p.price}{p.per && <span className="text-sm text-neutral-500 ml-2">{p.per}</span>}</div>
              <ul className="mt-5 space-y-2 text-sm text-neutral-300">
                {p.features.map((f) => <li key={f}>✓ {f}</li>)}
              </ul>
            </div>
          ))}
        </div>
        <div className="mt-8"><Link to="/pricing" data-testid="technology-pricing-link" className="text-sm text-blaze underline underline-offset-4">Full pricing details →</Link></div>
      </section>

      {/* Testimonial wall */}
      {testimonials.length > 0 && (
        <section className="py-16 md:py-24 border-t border-neutral-800" data-testid="testimonial-wall">
          <div className="max-w-6xl mx-auto px-5 md:px-8">
            <div className="text-xs tracking-widest text-blaze">FROM THE PROGRAMS</div>
            <h2 className="heading text-3xl md:text-5xl mt-2">WHAT ATHLETIC DIRECTORS SAY</h2>
            <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {testimonials.map((t) => (
                <figure key={t.id} className="border border-neutral-800 bg-appcard p-6 flex flex-col" data-testid={`testimonial-${t.id}`}>
                  <div className="text-blaze heading text-4xl leading-none">&ldquo;</div>
                  <blockquote className="text-neutral-300 text-sm leading-relaxed mt-2 flex-1">{t.quote}</blockquote>
                  <figcaption className="mt-5 pt-4 border-t border-neutral-800">
                    <div className="heading text-base">{t.author_name}</div>
                    <div className="text-[10px] tracking-widest text-neutral-500 mt-0.5">
                      {[t.author_title, t.program].filter(Boolean).join(" · ").toUpperCase()}
                    </div>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />

      {/* Sticky mobile conversion bar */}
      <div className="h-14 md:hidden" aria-hidden="true" />
      <div className="fixed bottom-0 inset-x-0 z-50 md:hidden bg-blaze text-white shadow-[0_-4px_20px_rgba(0,0,0,0.6)]" data-testid="mobile-cta-bar">
        <Link to="/auth?role=coach" data-testid="mobile-cta-link" className="flex items-center justify-center gap-2 py-4 heading text-lg">
          CLAIM YOUR COACH PROFILE — FREE <ArrowRight size={16} />
        </Link>
      </div>
    </div>
  );
}
